import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Sparkles } from 'lucide-react';

import { BACKEND_URL } from '../config/api';

/**
 * PartnerPlacement - Displays curated partner campaigns for a specific placement location
 * 
 * @param {string} placement - One of: 'no-results', 'employer-dashboard', 'tradesperson-dashboard', 'resource-pages'
 * @param {string} userType - 'employer', 'tradesperson', or 'all'
 * @param {string} region - User's region/postcode for targeting (optional)
 * @param {string} variant - 'card' (default), 'compact', 'banner'
 * @param {string} className - Additional CSS classes
 */
export default function PartnerPlacement({ 
  placement, 
  userType = 'all', 
  region = null,
  variant = 'card',
  className = ''
}) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlacements();
  }, [placement, userType, region]);

  const fetchPlacements = async () => {
    try {
      const params = new URLSearchParams();
      params.append('user_type', userType);
      if (region) params.append('region', region);

      const response = await axios.get(
        `${BACKEND_URL}/api/partners/partner-placements/${placement}?${params}`
      );
      setCampaigns(response.data.placements || []);
    } catch (error) {
      console.error('Failed to load partner placements');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (campaign) => {
    // Track click
    try {
      await axios.post(`${BACKEND_URL}/api/partners/partner-placements/${campaign.campaign_id}/click`);
    } catch (error) {
      console.error('Failed to track click');
    }
    
    // Open URL in new tab
    window.open(campaign.destination_url, '_blank', 'noopener,noreferrer');
  };

  if (loading || campaigns.length === 0) {
    return null; // Don't show anything if loading or no campaigns
  }

  // Compact variant for sidebars
  if (variant === 'compact') {
    return (
      <div className={`space-y-3 ${className}`}>
        {campaigns.map((campaign) => (
          <div 
            key={campaign.campaign_id}
            className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-3 hover:border-orange-200 hover:shadow-sm transition-all cursor-pointer"
            onClick={() => handleClick(campaign)}
            data-testid={`partner-compact-${campaign.campaign_id}`}
          >
            <div className="flex items-start gap-3">
              {campaign.logo_url && (
                <img 
                  src={campaign.logo_url} 
                  alt={campaign.partner_name}
                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-[10px] text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded font-medium">Partner</span>
                </div>
                <p className="text-sm font-medium text-slate-900 truncate">{campaign.partner_name}</p>
                <p className="text-xs text-slate-500 line-clamp-2">{campaign.short_description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Banner variant for prominent display
  if (variant === 'banner') {
    const campaign = campaigns[0]; // Only show first campaign in banner
    if (!campaign) return null;
    
    return (
      <div 
        className={`bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-5 relative overflow-hidden cursor-pointer group ${className}`}
        onClick={() => handleClick(campaign)}
        data-testid={`partner-banner-${campaign.campaign_id}`}
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="relative flex items-center gap-4">
          {campaign.logo_url && (
            <img 
              src={campaign.logo_url} 
              alt={campaign.partner_name}
              className="w-16 h-16 rounded-xl object-cover flex-shrink-0 border-2 border-white/20"
            />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-4 w-4 text-orange-400" />
              <span className="text-xs text-orange-400 font-medium">Featured Partner</span>
            </div>
            <h3 className="text-lg font-bold text-white mb-1">{campaign.partner_name}</h3>
            <p className="text-sm text-slate-300">{campaign.short_description}</p>
          </div>
          <button className="flex-shrink-0 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 group-hover:scale-105">
            {campaign.cta_text}
            <ExternalLink className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Default card variant
  return (
    <div className={`grid grid-cols-1 ${campaigns.length > 1 ? 'md:grid-cols-2' : ''} gap-4 ${className}`}>
      {campaigns.map((campaign) => (
        <div 
          key={campaign.campaign_id}
          className="bg-white border border-slate-200 rounded-xl p-4 hover:border-orange-200 hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => handleClick(campaign)}
          data-testid={`partner-card-${campaign.campaign_id}`}
        >
          <div className="flex items-start gap-4">
            {/* Logo */}
            <div className="flex-shrink-0">
              {campaign.logo_url ? (
                <img 
                  src={campaign.logo_url} 
                  alt={campaign.partner_name}
                  className="w-14 h-14 rounded-xl object-cover border border-slate-200"
                />
              ) : (
                <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-orange-500" />
                </div>
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full font-medium uppercase tracking-wide">
                  Partner
                </span>
                <span className="text-[10px] text-slate-400 capitalize">{campaign.category}</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-1 group-hover:text-orange-600 transition-colors">
                {campaign.partner_name}
              </h3>
              <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                {campaign.short_description}
              </p>
              <button className="text-sm font-medium text-orange-600 hover:text-orange-700 flex items-center gap-1.5 group-hover:gap-2 transition-all">
                {campaign.cta_text}
                <ExternalLink className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * PartnerBanner - Convenience component for banner variant
 */
export function PartnerBanner({ placement, userType, region, className }) {
  return (
    <PartnerPlacement 
      placement={placement} 
      userType={userType} 
      region={region} 
      variant="banner"
      className={className}
    />
  );
}

/**
 * PartnerSidebar - Convenience component for compact sidebar variant
 */
export function PartnerSidebar({ placement, userType, region, className }) {
  return (
    <PartnerPlacement 
      placement={placement} 
      userType={userType} 
      region={region} 
      variant="compact"
      className={className}
    />
  );
}
